/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
import { useConfigurationStore } from '@/stores/configurationModule';
import { useConfiguratorStore } from '@/stores/configurator';
import { useModalStore } from '@/stores/modal';
import { useKeyboardStore } from '@/stores/keyboard';

// Composables
import useInitFabricHistory from '@/composables/2dEditor/useInitFabricHistory';
import useZoomEventListeners from '@/composables/2dEditor/useZoomEventListeners';
import useObjectControlsEventListeners from '@/composables/2dEditor/useObjectControlsEventListeners';
import useScrollEventListeners from '@/composables/2dEditor/useScrollEventListeners';
import useWorkspaceEventListeners from '@/composables/2dEditor/useWorkspaceEventListeners';
import keyboard from '@/utils/keyboard';
import { scaleToBasePPI } from '@/utils/configuration';
import useNodeClient from '@/composables/services/useNodeClient';
const Adapter = useNodeClient();
import useWPClient from '@/composables/services/useWPClient';
const WPClient = useWPClient();
export default function useEditorEventListeners() {
  useZoomEventListeners();
  useObjectControlsEventListeners();
  useScrollEventListeners();
  useWorkspaceEventListeners();
  // EVENT BUS
  // ------------------------------------------------
  const $eventBus = inject('bus');
  // ------------------------------------------------

  // STORES
  // ------------------------------------------------
  const configurationStore = useConfigurationStore();
  const configuratorStore = useConfiguratorStore();
  const keyboardStore = useKeyboardStore();
  // ------------------------------------------------
  const {
    isSpacebarDown
  } = storeToRefs(keyboardStore);
  const {
    setConfiguration
  } = configurationStore;
  const {
    configuration,
    configurationId,
    variationId,
    activePanel,
    activeCanvas,
    fabricCanvases,
    activeObjects,
    boxType,
    boxWidth,
    boxLength,
    boxDepth,
    boxQuantity
  } = storeToRefs(configurationStore);
  const modalStore = useModalStore();
  const {
    setIsAddToCartModalOpen
  } = modalStore;
  const {
    setIsShiftDown,
    setIsMetaKeyDown,
    setIsSaving,
    setIsAddingToCart
  } = configuratorStore;
  // ------------------------------------------------

  // CONSTANTS (HTML Elements)
  // ------------------------------------------------
  const panels = Array.from(document.querySelectorAll('.panel-overlay'));
  // ------------------------------------------------

  // HISTORY
  // ------------------------------------------------
  useInitFabricHistory(fabricCanvases.value);
  // ------------------------------------------------

  // FUNCTIONS
  // ------------------------------------------------

  function handleDelete(e) {
    // @TODO: Refactor this
    const isText = activeObjects.value.length === 1 && ['i-text', 'text'].includes(activeObjects.value[0].objectType);
    let canRemoveText = true;
    if (isText) {
      canRemoveText = !activeObjects.value[0].isEditing;
    }
    // Don't know why delete won't work with the name and backspace wont't work with the code @TODO: Research
    if (e.which === 46 || e.key === "Backspace" && canRemoveText) {
      $eventBus.$emit('objects-removed');
    }
  }
  function handleEscapeKeyPress(e) {
    $eventBus.$emit('objects-deselected', e);
  }
  // ------------------------------------------------

  // EVENT BUS

  function prepareConfigurationForSaving() {
    let preparedCanvases = [];
    if (fabricCanvases.value && fabricCanvases.value.length) {
      preparedCanvases = fabricCanvases.value.map((canvas, index) => {
        const {
          objects
        } = canvas.toJSON();
        return {
          width: canvas.width,
          height: canvas.height,
          objects,
          position: index === 0 ? 'outside' : 'inside'
        };
      });
    }
    return scaleToBasePPI({
      meta: {
        ...configuration.value.meta
      },
      box: {
        boxType: boxType.value,
        boxWidth: boxWidth.value,
        boxLength: boxLength.value,
        boxDepth: boxDepth.value
      },
      canvases: preparedCanvases
    });
  }
  async function saveConfiguration() {
    setIsSaving(true);
    const dataForExport = prepareConfigurationForSaving();
    const id = configurationId.value;
    let data = {};
    if (id) {
      // This is update
      data = await Adapter.updateConfiguration(id, dataForExport);
      $eventBus.$emit('save-screenshot', id);
    } else {
      data = await Adapter.saveConfiguration(dataForExport);
      if (data) {
        setConfiguration(data); // ??
        $eventBus.$emit('save-screenshot', data._id); // @NOTE: before changing query?

        history.replaceState({}, '', `?id=${data._id}`);
      } else {
        // @NOTE: Proveri da li ima varId u query-u
        console.error('Failed to save configuration, no data returned.');
      }
    }
    setIsSaving(false);
    return data;
  }
  $eventBus.$on('save-config', async () => {
    // Saving state
    await saveConfiguration();
  });
  $eventBus.$on('add-to-cart', async () => {
    setIsSaving(true);
    setIsAddingToCart(true);
    const data = await saveConfiguration();
    if (!data) {
      console.log('Failed to save configuration');
      return;
    }
    const payload = {
      'product_id': variationId.value,
      'quantity': boxQuantity.value,
      'meta_data': {
        'configuration_id': data._id
      }
    };

    // @TODO - handle errors
    await WPClient.addToCart(payload);
    setIsSaving(false);
    setIsAddingToCart(false);
    setIsAddToCartModalOpen(true);
    $eventBus.$emit('open-add-to-cart-modal');
  });

  // ------------------------------------------------
  // TODO: Remove listeners before app is destroyed
  // Window event listeners
  window.addEventListener('keydown', event => {
    // This represents a spacebar keydown as event.keyCode is deprecated. See: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/keyCode
    if (keyboard.isSpacebar(event)) {
      isSpacebarDown.value = true;
      fabricCanvases.value.forEach(c => {
        c.upperCanvasEl.style.cursor = 'grab';
      });
      panels.forEach(p => {
        p.style.cursor = 'grab';
        if (p.id !== activePanel.value?.name) {
          p.style.boxShadow = 'none';
        }
      });
    }
    if (keyboard.isShift(event)) {
      setIsShiftDown(true);
    }
    if (keyboard.isMetaKey(event)) {
      setIsMetaKeyDown(true);
    }
    if (event.which === 46 || keyboard.isBackspace(event) || keyboard.isDelete(event)) {
      handleDelete(event);
    }
    if (event.which === 27 && keyboard.isEscape(event)) {
      handleEscapeKeyPress(event);
    }
  });
  window.addEventListener('keyup', event => {
    if (keyboard.isSpacebar(event)) {
      isSpacebarDown.value = false;
      fabricCanvases.value.forEach(c => {
        c.upperCanvasEl.style.cursor = 'default';
      });
      panels.forEach(p => {
        p.style.cursor = 'default';
        p.style.boxShadow = '';
      });
    }
    if (keyboard.isShift(event)) {
      setIsShiftDown(false);
    }
    if (keyboard.isMetaKey(event)) {
      setIsMetaKeyDown(false);
    }
  });

  // Document event listeners
  document.addEventListener('keydown', event => {
    if (keyboard.isUndo(event)) {
      event.preventDefault();
      activeCanvas.value.undo();
    }
    if (keyboard.isRedo(event)) {
      event.preventDefault();
      activeCanvas.value.redo();
    }
    if (keyboard.isSave(event)) {
      event.preventDefault();
      $eventBus.$emit('save-config');
    }
    if (keyboard.isMetaKey(event)) {
      // setIsMetaKeyDown(true)
    }
  });
  document.addEventListener('keyup', event => {
    if (keyboard.isUndo(event)) {
      event.preventDefault();
      activeCanvas.value.undo();
    }
    if (keyboard.isRedo(event)) {
      event.preventDefault();
      activeCanvas.value.redo();
    }
    if (keyboard.isSave(event)) {
      event.preventDefault();
      $eventBus.$emit('save-config');
    }
    if (keyboard.isMetaKey(event)) {
      // setIsMetaKeyDown(false)
    }
  });

  // ------------------------------------------------
}